import { IconMenuOutline } from '@icons/IconMenuOutline'
import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { CellContext } from '@tanstack/react-table'
import { RowAction, TableUiSchema } from '../types-v4'
import { ActionItemNoConfirmation } from './ActionItemNoConfirmation'
import { ActionWithConfirmDialog } from './ActionWithConfirmDialog'
import { EditMenuItem } from './EditMenuItem'
import { UploadTemplateMenuItem } from './UploadTemplateMenuItem'
import { CollaborationMenuItem } from './CollaborationMenuItem'

// Group actions are now decoupled from schema and always include Edit and Delete
const GROUP_ROW_ACTIONS: RowAction[] = [
  {
    id: 'edit',
    label: 'Edit',
    requiresSelection: false,
    visible: true,
    confirmationRequired: false, // Groups never confirm edits
    type: 'edit',
    handlerId: 'local-group-edit', // Dummy handlerId to satisfy component checks
  },
  {
    id: 'delete',
    label: 'Delete',
    requiresSelection: false,
    visible: true,
    confirmationRequired: false, // No confirmation for local group delete
    type: 'delete',
    handlerId: 'local-group-delete', // Dummy handlerId to satisfy component checks
  },
]

export function RowActionsMenu<T, V>({ info, tableSchema }: { info: CellContext<T, V>; tableSchema?: TableUiSchema }) {
  const rowActions = tableSchema?.actions?.row

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const meta = info.table.options.meta as any
  const isRegrouping = !!meta?.isRegrouping
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isGroup = !!(info.row.original as any).isGroup

  // Visibility logic:
  // - isRegrouping=true: Show ONLY on group rows
  // - isRegrouping=false: Show ONLY on subrows/orphaned rows (non-group)
  if (isRegrouping !== isGroup) return null

  // For group rows, use the hardcoded GROUP_ACTIONS, ignoring schema actions
  const displayActions = isGroup ? GROUP_ROW_ACTIONS : rowActions

  if (!displayActions) return null

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        // This is necessary to maintain the open state when we want to keep it open
        if (!open) {
          // Check if we need to prevent closing
          // This is a placeholder for custom logic you might need
          // We'll use other mechanisms to prevent closing on item click
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-8">
          <IconMenuOutline size={16} />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onCloseAutoFocus={(e) => {
          // Prevent focus returning to trigger which can close the menu
          e.preventDefault()
        }}
      >
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {displayActions.map((action) => {
          // Edit action
          if (action.type === 'edit' || action.id === 'edit') {
            // For groups, we ensure confirmationRequired is false (already set in GROUP_ACTIONS constant)
            // and pass schema so it can find handlers/config if needed, though for groups it uses local update
            return <EditMenuItem key={action.id} action={action} info={info} tableSchema={tableSchema} />
          }

          // Standard actions below
          if (action.type === 'uploadtemplate' || action.id === 'uploadtemplate') {
            return <UploadTemplateMenuItem action={action} info={info} key={action.id} />
          }
          if (action.type === 'collaboration' || action.id === 'collaboration') {
            return <CollaborationMenuItem action={action} info={info} key={action.id} />
          }

          // If confirmation is NOT required, OR if it's a group delete action (which we've forced to no confirm)
          // Note: Group Delete was forced to confirmationRequired: false in GROUP_ACTIONS, so it will fall into this block
          if (!action.confirmationRequired) {
            return <ActionItemNoConfirmation action={action} info={info} key={action.id} />
          }

          // Default fallback for actions requiring confirmation
          return (
            <DropdownMenuItem key={action.id} onSelect={(e) => e.preventDefault()}>
              <ActionWithConfirmDialog action={action} info={info} />
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
